import React, { useCallback, useEffect } from "react";
import { Badge, Dropdown, Spinner } from "@optimizely/axiom";

import { useUserContext } from "../../../providers/UserProvider";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { useUsers } from "../../../hooks/useUsers/useUsers";
import { SearchInput } from "../SearchInput/SearchInput";
import { User } from "../../../domain/User";
import { DEFAULT_DEBOUNCE } from "../../../constants/constants";
import styles from "./UserSearchSelector.module.scss";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import { ExternalUserIndicator } from "../ExternalUserIndicator/ExternalUserIndicator";
import { AgentUserIndicator } from "../AgentUserIndicator/AgentUserIndicator";

export const UserSearchSelector = ({
    clearOnSelect = true,
    disabled = false,
    isUserDisabled,
    lowerCaseInput = false,
    onSelectUser,
    onChange,
    placeholder,
    value = ""
}: {
    disabled?: boolean;
    clearOnSelect?: boolean;
    isUserDisabled?: (user: User) => boolean;
    lowerCaseInput?: boolean;
    onSelectUser?: (user?: User) => void;
    onChange?: (email: string) => void;
    placeholder?: string;
    value?: string;
}) => {
    const { organizationId } = useUserContext();
    const { enabled: showExternalStatus } = useFeatureFlag(Flags.ENABLE_EXTERNAL_USER_STATUS_DISPLAY);
    const { enabled: enableVirtualAgentUsers } = useFeatureFlag(Flags.ENABLE_VIRTUAL_AGENT_USERS);
    const { enabled: enableRebrand } = useFeatureFlag(Flags.ENABLE_REBRAND);

    const {
        debouncedValue: debouncedUserQuery,
        value: userQuery,
        setValue: setUserQuery
    } = useDebounce(value, DEFAULT_DEBOUNCE);

    const { userList, isSearching } = useUsers({
        organizationId,
        query: debouncedUserQuery.length >= 2 ? debouncedUserQuery : ""
    });

    const searchList = userList && debouncedUserQuery.length >= 2 && !isSearching ? userList : [];
    const noResults = searchList.length === 0 && debouncedUserQuery.length >= 2 && !isSearching;

    const searchUsers = (input: { value: string }) => {
        const emittedValue = lowerCaseInput ? input.value.toLowerCase() : input.value;
        setUserQuery(emittedValue);
        onChange && onChange(emittedValue);
    };

    const renderNoResult = () => {
        return noResults ? <div className="soft">No matching users</div> : null;
    };

    useEffect(() => {
        setUserQuery(value);
    }, [value, setUserQuery]);

    const renderSearchInput = ({
        onBlur,
        onClick,
        ref
    }: {
        onBlur: (...args: any[]) => any;
        onClick: (...args: any[]) => any;
        ref: React.Ref<HTMLInputElement>;
    }) => (
        <SearchInput
            ariaLabel="Search for users..."
            inputRef={ref}
            disabled={disabled}
            placeholder={placeholder || "Search for users..."}
            value={userQuery}
            onBlur={onBlur}
            onChange={searchUsers}
            onClick={onClick}
        />
    );

    const getLineItemContent = useCallback(
        (user: User) => {
            const isExternal = user.homeOrganizationId !== organizationId;
            // Virtual users are given a placeholder last name by Opal, so only the
            // first name is surfaced for them.
            const isVirtualUser = enableVirtualAgentUsers && user.isVirtual;
            const displayName = isVirtualUser ? user.firstName : `${user.firstName} ${user.lastName}`;

            if (enableVirtualAgentUsers && enableRebrand) {
                return (
                    <>
                        {displayName} ({user.email})
                        {user.isVirtual && (
                            <Badge color="purple" className="oui-badge--virtual-teammate push--left">
                                Virtual Teammate
                            </Badge>
                        )}
                        {!user.isVirtual && isExternal && (
                            <Badge className="oui-badge--external push--left">External</Badge>
                        )}
                    </>
                );
            }

            const showExternal = (showExternalStatus || enableVirtualAgentUsers) && isExternal;
            const showAgent = enableVirtualAgentUsers && user.isVirtual;
            const enabled = (isUserDisabled && !isUserDisabled(user)) || !isUserDisabled;
            const fill = enabled ? undefined : "#808080";
            return (
                <>
                    {displayName} ({user.email}){showAgent && <AgentUserIndicator className="push--left" fill={fill} />}
                    {showExternal && <ExternalUserIndicator className="push--left" fill={fill} />}
                </>
            );
        },
        [enableVirtualAgentUsers, enableRebrand, showExternalStatus, organizationId, isUserDisabled]
    );

    return (
        <Dropdown renderActivator={renderSearchInput} fullWidth>
            <Dropdown.Contents isLoading={isSearching} renderHeader={renderNoResult} style={{ width: "100%" }}>
                {searchList.map((user) => (
                    <Dropdown.ListItem key={user.id}>
                        {(isUserDisabled && !isUserDisabled(user)) || !isUserDisabled ? (
                            <Dropdown.BlockLink
                                onClick={() => {
                                    clearOnSelect ? setUserQuery("") : setUserQuery(user.email);
                                    onSelectUser && onSelectUser(user);
                                }}
                            >
                                <div className="truncated">{getLineItemContent(user)}</div>
                            </Dropdown.BlockLink>
                        ) : (
                            <Dropdown.BlockLink
                                onClick={() => {
                                    onSelectUser && onSelectUser();
                                }}
                            >
                                <div
                                    data-cy="user-search-selecter-disabled"
                                    className={styles["user-search-selector__userdisabled"]}
                                >
                                    {getLineItemContent(user)}
                                </div>
                            </Dropdown.BlockLink>
                        )}
                    </Dropdown.ListItem>
                ))}
                {isSearching && <Spinner hasOverlay />}
            </Dropdown.Contents>
        </Dropdown>
    );
};

UserSearchSelector.displayName = "UserSearchSelector";
